import {MultiAllocationSheetDetails} from "src/models/multidimensional-allocation/MultiAllocationSheetDetails";
import {Box, Button, ColumnLayout, Header, SpaceBetween, StatusIndicator, Link, Select, FormField} from "@amzn/awsui-components-react";
import React, {useState, useContext, useEffect} from "react";
import {STATUS_INDICATOR_CONFIG} from "src/components/multidimensional-allocation/common/Constants";
import {WorksheetStatus} from "src/models/multidimensional-allocation/MultiAllocationSheetData";
import { ApprovalType } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { ProcessApprovalModal } from '../shared/ProcessApprovalModal';
import {useApprovalButtonProps} from "src/components/multidimensional-allocation/hooks/useApprovalButtonProps";
import { LinkedEntitiesModal } from '../shared/LinkedEntitiesModal';
import { EntityType } from 'src/models/multidimensional-allocation/EntityType';
import { GlobalAppContext } from 'src/components/App';

export interface MDASheetViewHeadingProps {
    sheetData: MultiAllocationSheetDetails;
    availableVersions?: number[];
    selectedVersion?: number;
    onVersionChange?: (selectedOption: any) => void;
    loading?: boolean;
}
export function MDASheetViewHeading(props: MDASheetViewHeadingProps) {
    const { sheetData, availableVersions = [], selectedVersion, onVersionChange, loading = false } = props;
    const { services } = useContext(GlobalAppContext);
    const [modalMode, setModalMode] = useState<'closed' | 'submit' | 'approval'>('closed');
    
    // State for linked entities modal
    const [isLinkedEntitiesModalVisible, setIsLinkedEntitiesModalVisible] = useState<boolean>(false);
    const [linkedEntitiesLoadTrigger, setLinkedEntitiesLoadTrigger] = useState<string>('');

    // Linked entities API call
    const [linkedEntitiesResponse, linkedEntitiesLoading, linkedEntitiesError] = services.multidimensionalAllocationService.getMultiAllocationLinkedEntities(
        EntityType.MULTI_ALLOCATION_SHEET,
        sheetData.sheetId,
        linkedEntitiesLoadTrigger
    );

    // Trigger fetching linked entities when component mounts
    useEffect(() => {
        if (sheetData.sheetId) {
            setLinkedEntitiesLoadTrigger(new Date().toISOString());
        }
    }, [sheetData.sheetId]);

    // Handle linked entities error
    useEffect(() => {
        if (linkedEntitiesError) {
            services.messageService.showErrorBanner(`Failed to load linked entities: ${linkedEntitiesError}`);
        }
    }, [linkedEntitiesError, services.messageService]);

    // Handle success callback
    const handleSuccess = () => {
        window.location.reload();
    };

    // Handle linked entities link click
    const handleLinkedEntitiesClick = () => {
        setIsLinkedEntitiesModalVisible(true);
    };

    // Handle linked entities modal close
    const handleLinkedEntitiesModalClose = () => {
        setIsLinkedEntitiesModalVisible(false);
    };

    // Get linked entities count
    const linkedWorksheetCount = linkedEntitiesResponse?.linkedEntities?.length || 0;

    // Get button properties using the reusable hook
    const actionButtonProps = useApprovalButtonProps(
        sheetData.status,
        () => setModalMode('submit'),
        () => setModalMode('approval'),
        sheetData.validationSummary,
        undefined // Sheets don't have execution groups
    );

    const getStatusIndicator = (status: string) => {
        const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
        return <StatusIndicator type={statusType} colorOverride={color}>
            {label}
        </StatusIndicator>
    }

    const formatSegmentType = (segmentType: string) => {
        if (!segmentType) return '-';
        return segmentType.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const displayAttribute = (key: string, value: React.ReactNode) => {
        return (<SpaceBetween direction={"horizontal"} size={'xs'}>
            <Box variant="awsui-key-label"> {key}  </Box>
            <div> {value || '-'} </div>
        </SpaceBetween>)
    }

    // Create version options from available versions
    const getVersionOptions = () => {
        return availableVersions.map((version: number) => ({
            label: `Version ${version}`,
            value: version.toString()
        }));
    };

    const getCurrentVersionOption = () => {
        if (selectedVersion !== undefined) {
            return {
                label: `Version ${selectedVersion}`,
                value: selectedVersion.toString()
            };
        }
        return null;
    };

    return (<SpaceBetween size="l" direction="vertical">
            <Header
                variant="h1"
                description={
                    <Box color='text-body-secondary'>
                        { "View and manage a multidimensional allocation sheet." }
                    </Box>
                }
            >
                { sheetData.sheetName }
            </Header>
            <Header
                actions={
                    <>
                        <Button
                            variant="primary"
                            onClick={actionButtonProps.onClick}
                            disabled={actionButtonProps.disabled}
                            disabledReason={actionButtonProps.disabledReason}
                        >
                            {actionButtonProps.label}
                        </Button>

                        <ProcessApprovalModal
                            entityId={sheetData.sheetId}
                            approvalType={ApprovalType.MULTI_ALLOCATION_SHEET}
                            validationData={sheetData.validationSummary}
                            mode={modalMode}
                            onModeChange={setModalMode}
                            onSuccess={handleSuccess}
                        />
                    </>
                }
            >
                <ColumnLayout columns={availableVersions.length > 1 ? 6 : 5} borders='vertical'>
                    {displayAttribute("Sheet status", getStatusIndicator(sheetData.status))}
                    {displayAttribute("Segment Type", formatSegmentType(sheetData.segmentType))}
                    <Box variant="awsui-key-label">
                        <Link
                            variant="primary"
                            onFollow={handleLinkedEntitiesClick}
                        >
                            {linkedWorksheetCount} Linked Worksheet(s)
                        </Link>
                    </Box>
                    {availableVersions.length > 1 && (
                        <FormField>
                            <Select
                                selectedOption={getCurrentVersionOption()}
                                onChange={onVersionChange}
                                options={getVersionOptions()}
                                placeholder="Select version"
                                disabled={loading}
                            />
                        </FormField>
                    )}
                </ColumnLayout>
            </Header>

            {/* Linked Entities Modal */}
            <LinkedEntitiesModal
                visible={isLinkedEntitiesModalVisible}
                entityName={sheetData.sheetName || ''}
                entityType={EntityType.MULTI_ALLOCATION_SHEET}
                linkedEntities={linkedEntitiesResponse?.linkedEntities || []}
                linkedCount={linkedWorksheetCount}
                onDismiss={handleLinkedEntitiesModalClose}
            />
        </SpaceBetween>
    )
}
